import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Truck, Package, TrendingUp, Bell, CheckCircle, Clock, Info, DollarSign, List, FileText, AlertCircle, Edit, Bookmark, Map, RotateCcw } from 'lucide-react';
import BidModal from './BidModal';
import BidList from './BidList';
import LoadDetails from './LoadDetails';
import { useSelector } from 'react-redux';
import { selectUserEntityId } from '../store/slices/authSlice';
import EditLoadModal from './EditLoadModal';
import api from '../utils/api';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer } from 'react-toastify';
import { LoadActionButtons } from './LoadActionButtons';
import useScreenSize from '../hooks/useScreenSize';
import MapModal from './MapModal';
import { parseDateTime, formatDate, formatMilitaryTime, formatDateTime } from '../utils/dateTimeUtils';
import { computeFirstPickup, computeLastDelivery, getLoadDisplayId, isLoadExpired } from '../utils/loadUtils';

// Helper function to safely format dates without timezone issues
const safeFormatDate = (date) => {
  if (!date) return null;
  
  try {
    // Handle different date formats that might come from backend
    let dateToFormat = date;
    
    // If it's an ISO string with timezone, extract just the date part
    if (typeof date === 'string' && date.includes('T')) {
      dateToFormat = date.split('T')[0];
    }
    
    // Use the improved formatDate function that handles timezone issues
    return formatDate(dateToFormat);
  } catch (error) {
    console.error('Date formatting error:', error, { date });
    return null;
  }
};

// Helper functions to get first pickup and last delivery from stops
const getFirstPickup = (load) => {
  if (!load.stops || load.stops.length === 0) {
    // Fallback to legacy pickup data
    return {
      date: load.pickup_date,
      time: load.pickup_time,
      city: load.origin_city,
      state: load.origin_state,
      address: load.origin_address
    };
  }
  
  // Find first pickup stop (lowest sequence with type 'pickup')
  const pickupStops = load.stops.filter(stop => stop.stop_type === 'pickup');
  if (pickupStops.length === 0) {
    // If no pickup stops found, use first stop
    const firstStop = load.stops.sort((a, b) => a.sequence - b.sequence)[0];
    return {
      // Prefer load-level overrides if present (e.g., after accepting alt bid)
      date: load.pickup_date || firstStop.date,
      time: load.pickup_time || firstStop.time,
      city: firstStop.city,
      state: firstStop.state,
      address: firstStop.address
    };
  }
  
  const firstPickup = pickupStops.sort((a, b) => a.sequence - b.sequence)[0];
  return {
    // Prefer load-level overrides if present (e.g., after accepting alt bid)
    date: load.pickup_date || firstPickup.date,
    time: load.pickup_time || firstPickup.time,
    city: firstPickup.city,
    state: firstPickup.state,
    address: firstPickup.address
  };
};

const getLastDelivery = (load) => {
  if (!load.stops || load.stops.length === 0) {
    // Fallback to legacy delivery data
    return {
      date: load.delivery_date,
      time: load.delivery_time,
      city: load.destination_city,
      state: load.destination_state,
      address: load.destination_address
    };
  }
  
  // Find last delivery stop (highest sequence with type 'delivery')
  const deliveryStops = load.stops.filter(stop => stop.stop_type === 'delivery');
  if (deliveryStops.length === 0) {
    // If no delivery stops found, use last stop
    const lastStop = load.stops.sort((a, b) => b.sequence - a.sequence)[0];
    return {
      // Prefer load-level overrides if present (e.g., after accepting alt bid)
      date: load.delivery_date || lastStop.date,
      time: load.delivery_time || lastStop.time,
      city: lastStop.city,
      state: lastStop.state,
      address: lastStop.address
    };
  }
  
  const lastDelivery = deliveryStops.sort((a, b) => b.sequence - a.sequence)[0];
  return {
    // Prefer load-level overrides if present (e.g., after accepting alt bid)
    date: load.delivery_date || lastDelivery.date,
    time: load.delivery_time || lastDelivery.time,
    city: lastDelivery.city,
    state: lastDelivery.state,
    address: lastDelivery.address
  };
};

export const DashboardCard = ({ icon: Icon, title, value, trend, trendValue }) => (
  <motion.div 
    className="bg-white rounded-xl shadow-lg p-3 sm:p-6"
    whileHover={{ y: -5, boxShadow: '0 10px 30px -15px rgba(0, 0, 0, 0.2)' }}
  >
    <div className="flex flex-col sm:flex-row sm:items-center">
      <div className="bg-indigo-100 p-2 sm:p-3 rounded-full mb-2 sm:mb-0 sm:mr-4 w-fit">
        <Icon className="w-6 h-6 sm:w-8 sm:h-8 text-indigo-600" />
      </div>
      <div>
        <h3 className="text-sm sm:text-lg font-semibold text-gray-700">{title}</h3>
        <p className="text-lg sm:text-2xl font-bold text-gray-900">{value}</p>
      </div>
    </div>
    {trend && (
      <div className={`flex items-center mt-2 sm:mt-0 ${trend === 'up' ? 'text-green-500' : 'text-red-500'}`}>
        <ChevronDown className={`w-4 h-4 sm:w-5 sm:h-5 ${trend === 'up' ? 'transform rotate-180' : ''}`} />
        <span className="ml-1 text-xs sm:text-sm font-medium">{trendValue}</span>
      </div>
    )}
  </motion.div>
);

// New component for load header
const LoadHeader = ({ load, loadType }) => (
  <div className="flex flex-col gap-2 mb-4">
    <div className="flex items-start justify-between">
      <h3 className="text-xl font-semibold text-gray-800">
        {`${load.origin_city}, ${load.origin_state} to ${load.destination_city}, ${load.destination_state}`}
      </h3>
    </div>
    <div className="flex items-center gap-3">
      <p className="text-sm text-gray-500">Load ID: {getLoadDisplayId(load)}</p>
      <span className="inline-flex items-center text-sm font-medium text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
        {loadType}
      </span>
    </div>
  </div>
);

// New component for location details
const LocationDetails = ({ load, isCustomer }) => (
  <div className="text-sm text-gray-500 space-y-1">
    <p className="flex items-center">
      <Truck className="w-4 h-4 mr-1" />
      From: {isCustomer ? `${load.origin_address}, ${load.origin_zip}` : `${load.origin_city}, ${load.origin_state}`}
    </p>
    <p className="flex items-center">
      <Package className="w-4 h-4 mr-1" />
      To: {isCustomer ? `${load.destination_address}, ${load.destination_zip}` : `${load.destination_city}, ${load.destination_state}`}
    </p>
  </div>
);

// Add new BrandStamp component near other helper components
const BrandStamp = () => (
  <div className="absolute bottom-3 right-4 sm:right-6 flex items-center gap-1.5 opacity-40 select-none">
    <div className="w-2 h-2 rounded-full bg-indigo-600" />
    <span className="text-xs font-medium text-gray-500 tracking-wider">LOAD LINK</span>
  </div>
);

// Main LoadItem component refactored
export const LoadItem = ({ load, user, onSaveLoad }) => {
  const [modals, setModals] = useState({
    bid: false,
    bidList: false,
    loadDetails: false,
    edit: false,
    map: false
  });

  const userEntityId = useSelector(selectUserEntityId);
  const isViewOnly = user?.isViewOnly || false;
  const userRoles = {
    isCarrier: ['carrier', 'carrier_admin'].includes(user.role),
    isCustomer: ['customer', 'customer_admin'].includes(user.role)
  };

  // Use centralized isLoadExpired utility from loadUtils.js
  // This properly parses the ISO8601 pickup_datetime for accurate timezone handling
  const expired = isLoadExpired(load);

  const loadStatus = {
    isAssigned: (load.status || '').toLowerCase() === 'assigned',
    hasActiveBids: userRoles.isCarrier && load.bids?.some(bid => 
      bid.carrier_id.toString() === userEntityId.substring(1)
    ),
    carrierBidCount: userRoles.isCarrier
      ? load.bids?.filter(bid => bid.carrier_id.toString() === userEntityId.substring(1)).length || 0
      : 0,
    isExpired: expired
  };

  const containerClasses = `
    bg-white shadow-lg rounded-xl p-0 mb-6 
    transition-all duration-300 relative overflow-hidden
    ${loadStatus.isAssigned ? 'ring-2 ring-green-500' : ''}
    ${!loadStatus.isAssigned && loadStatus.hasActiveBids ? 'ring-2 ring-blue-500' : ''}
    ${!loadStatus.isAssigned && !loadStatus.hasActiveBids && load.is_saved ? 'ring-2 ring-yellow-400' : ''}
    ${load.resubmitted ? 'ring-2 ring-purple-400 bg-gradient-to-br from-white to-purple-50/30' : ''}
    hover:shadow-xl
  `;

  const toggleModal = (modalName, value) => {
    setModals(prev => ({ ...prev, [modalName]: value }));
  };

  const handleSaveLoad = async () => {
    onSaveLoad(load);
  };

  const StatusIndicators = () => (
    <div className="flex flex-wrap gap-2 justify-end sm:justify-center mb-3 sm:mb-0">
      {load.resubmitted && (
        <div
          className="bg-purple-50 text-purple-600 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full flex items-center gap-1 sm:gap-1.5 text-xs sm:text-sm"
        >
          <RotateCcw className="w-3 h-3 sm:w-4 sm:h-4" />
          <span className="font-medium">Resubmitted</span>
        </div>
      )}
      {loadStatus.isExpired && !load.resubmitted && (
        <div
          className="bg-orange-50 text-orange-600 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full flex items-center gap-1 sm:gap-1.5 text-xs sm:text-sm"
        >
          <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4" />
          <span className="font-medium">Expired</span>
        </div>
      )}
      {load.is_saved && (
        <div
          className="bg-yellow-50 text-yellow-600 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full flex items-center gap-1 sm:gap-1.5 text-xs sm:text-sm"
        >
          <Bookmark className="w-3 h-3 sm:w-4 sm:h-4 fill-current" />
          <span className="font-medium">Saved</span>
        </div>
      )}
      {loadStatus.hasActiveBids && (
        <div
          className="bg-blue-50 text-blue-600 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full flex items-center gap-1 sm:gap-1.5 text-xs sm:text-sm"
        >
          <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4" />
          <span className="font-medium">Active Bid</span>
        </div>
      )}
      {loadStatus.isAssigned && (
        <div
          className="bg-green-50 text-green-600 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full flex items-center gap-1 sm:gap-1.5 text-xs sm:text-sm"
        >
          <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" />
          <span className="font-medium">Assigned</span>
        </div>
      )}
    </div>
  );

  const { isMobile } = useScreenSize();

  return (
    <>
      <motion.div className={containerClasses}>
        <div className="border-b border-gray-100">
          <div className="px-4 pt-4 sm:px-6 sm:pt-6">
            <StatusIndicators />
          </div>
          
          <div className="px-4 pb-4 sm:px-6 sm:pb-6">
            <LoadHeader load={load} loadType={load.load_type} />
            <LocationDetails load={load} isCustomer={userRoles.isCustomer} />
          </div>
        </div>

        {/* Main Content Grid - Mobile Responsive */}
        <div className="p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-12 gap-6">
          {/* Schedule Section */}
          <div className="sm:col-span-4 space-y-4">
            <SectionTitle icon={Clock} title="Schedule" />
            <div className="space-y-3">
              {(() => {
                const firstPickup = computeFirstPickup(load);
                const lastDelivery = computeLastDelivery(load);
                return (
                  <>
                    <TimelineItem
                      label="First Pickup"
                      date={firstPickup.date}
                      time={firstPickup.time}
                      location={`${firstPickup.city}, ${firstPickup.state}`}
                    />
                    <TimelineItem
                      label="Final Delivery"
                      date={lastDelivery.date}
                      time={lastDelivery.time}
                      location={`${lastDelivery.city}, ${lastDelivery.state}`}
                    />
                  </>
                );
              })()}
            </div>
          </div>

          {/* Cargo Details Section */}
          <div className="sm:col-span-4 space-y-4">
            <SectionTitle icon={Package} title="Cargo Details" />
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <DetailCard label="Weight" value={`${load.weight} lbs`} />
              <DetailCard label="Pieces" value={load.pieces} />
              <DetailCard 
                label="Dimensions" 
                value={load.dimensions ? `${load.dimensions.length}x${load.dimensions.width}x${load.dimensions.height}` : 'N/A'} 
              />
              <DetailCard 
                label="Type" 
                value={load.load_type} 
              />
            </div>
          </div>

          {/* Requirements Section */}
          <div className="sm:col-span-4 space-y-4">
            <SectionTitle icon={AlertCircle} title="Requirements" />
            <div className="space-y-3">
              {load.special_requirements && (
                <Alert variant="warning" icon={Bell}>
                  {load.special_requirements}
                </Alert>
              )}
              {load.description && (
                <Alert variant="info" icon={Info}>
                  {load.description}
                </Alert>
              )}
            </div>
          </div>
        </div>

        {/* Actions Footer - Mobile Responsive */}
        <div className="border-t border-gray-100 px-4 py-3 sm:px-6 sm:py-4 bg-gray-50 relative">
          <div className="flex flex-col gap-3">
            {/* Primary Actions - Top Row */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => toggleModal('loadDetails', true)}
                className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-2 
                          text-gray-700 bg-white hover:bg-gray-50 border border-gray-200 
                          rounded-lg transition-colors duration-200 font-medium text-sm shadow-sm"
              >
                <FileText className="w-4 h-4" />
                <span className="hidden sm:inline">Quick Preview</span>
                <span className="sm:hidden">Preview</span>
              </button>
              <button
                onClick={() => toggleModal('map', true)}
                className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-2 
                          text-indigo-600 bg-indigo-50 hover:bg-indigo-100 
                          rounded-lg transition-colors duration-200 font-medium text-sm"
              >
                <Map className="w-4 h-4" />
                <span className="hidden sm:inline">View Route</span>
                <span className="sm:hidden">Route</span>
              </button>
              {userRoles.isCarrier && (
                <button
                  onClick={handleSaveLoad}
                  className={`
                    flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors
                    ${load.is_saved 
                      ? 'text-blue-600 bg-blue-50 hover:bg-blue-100' 
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'}
                  `}
                >
                  <Bookmark className={`w-4 h-4 ${load.is_saved ? 'fill-current' : ''}`} />
                  <span>{load.is_saved ? 'Saved' : 'Save'}</span>
                </button>
              )}
            </div>

            {/* Secondary Actions - Bottom Row */}
            <div className="flex flex-wrap gap-2 w-full">
              {userRoles.isCustomer && (
                <LoadActionButtons 
                  load={load} 
                  isMobile={isMobile}
                  canShare={true}
                  viewButtonText="Full Details"
                  viewButtonIcon={List}
                  className="w-full sm:w-auto"
                />
              )}
              {!isViewOnly && (
                <button
                  onClick={() => toggleModal('bidList', true)}
                  className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-2
                            text-gray-700 bg-gray-100 hover:bg-gray-200 
                            rounded-lg transition-colors duration-200 font-medium text-sm"
                >
                  <List className="w-4 h-4" />
                  <span className="hidden sm:inline">
                    {userRoles.isCustomer 
                      ? `View Bids (${load.bids?.length || 0})` 
                      : `My Bids (${loadStatus.carrierBidCount})`
                    }
                  </span>
                  <span className="sm:hidden">
                    {userRoles.isCustomer 
                      ? `Bids (${load.bids?.length || 0})` 
                      : `Bids (${loadStatus.carrierBidCount})`
                    }
                  </span>
                </button>
              )}
              {userRoles.isCarrier && !loadStatus.isAssigned && (
                <button
                  onClick={() => toggleModal('bid', true)}
                  className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-2
                            text-white bg-blue-600 hover:bg-blue-700 
                            rounded-lg transition-colors duration-200 font-medium text-sm"
                >
                  <DollarSign className="w-4 h-4" />
                  <span>Place Bid</span>
                </button>
              )}
            </div>
          </div>
          <BrandStamp />
        </div>
      </motion.div>

      {/* Modals */}
      <AnimatePresence>
        {modals.bid && !isViewOnly && (
          <BidModal
            load={load}
            isOpen={modals.bid}
            onClose={() => toggleModal('bid', false)}
          />
        )}
        {modals.bidList && !isViewOnly && (
          <BidList 
            loadId={load.id}
            isOpen={modals.bidList}
            onClose={() => toggleModal('bidList', false)}
            userRole={user.role}
            loadBids={load.bids}
            isCustomer={userRoles.isCustomer}
          />
        )}
        {modals.loadDetails && (
          <LoadDetails 
            load={load}
            isOpen={modals.loadDetails}
            onClose={() => toggleModal('loadDetails', false)}
            showFullAddress={userRoles.isCustomer}
          />
        )}
        {modals.edit && (
          <EditLoadModal
            load={load}
            isOpen={modals.edit}
            onClose={() => toggleModal('edit', false)}
          />
        )}
        {modals.map && (
          <MapModal
            isOpen={modals.map}
            onClose={() => toggleModal('map', false)}
            stops={load.stops}
          />
        )}
      </AnimatePresence>
    </>
  );
};

// New Helper Components
const SectionTitle = ({ icon: Icon, title }) => (
  <div className="flex items-center gap-2 text-gray-800 font-semibold">
    <Icon className="w-4 h-4" />
    <span>{title}</span>
  </div>
);

const TimelineItem = ({ label, date, time, location }) => {
  // Use the safe formatting function to match LoadDetails.js behavior
  const formattedDate = safeFormatDate(date);
  const formattedTime = formatMilitaryTime(time);

  return (
    <div className="relative pl-6 border-l-2 border-gray-200">
      <div className="absolute w-3 h-3 bg-blue-500 rounded-full -left-[7px] top-1" />
      <p className="font-medium text-gray-800">{label}</p>
      <p className="text-sm text-gray-600">{formattedDate || 'N/A'}</p>
      <p className="text-sm text-gray-600">{formattedTime || 'N/A'}</p>
      <p className="text-sm text-gray-500">{location}</p>
    </div>
  );
};

const DetailCard = ({ label, value }) => (
  <div className="bg-gray-50 p-3 rounded-lg">
    <p className="text-sm text-gray-500">{label}</p>
    <p className="font-medium text-gray-800">{value}</p>
  </div>
);

const Alert = ({ variant, icon: Icon, children }) => {
  const variants = {
    warning: 'bg-amber-50 text-amber-700 border-amber-200',
    info: 'bg-blue-50 text-blue-700 border-blue-200'
  };

  return (
    <div className={`p-3 rounded-lg border ${variants[variant]} flex gap-2`}>
      <Icon className="w-5 h-5 flex-shrink-0" />
      <p className="text-sm">{children}</p>
    </div>
  );
};